import {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import styled, { css } from 'styled-components'

import config from '@config'
import { mobileCSS } from '@measures/responsive'
import nanoid from '@utils/random'
import { getAppTheme } from '@hooks/useAppTheme'
import useCMPCookieCategories from '@hooks/useCMPCookieCategories'
import useDisableThirdPartyScripts from '@hooks/useDisableThirdPartyScripts'
import useIframeRefs from '@hooks/useIframeRefs'
import useIsAuthResolved from '@hooks/useIsAuthResolved'
import useRefDelegate from '@hooks/useRefDelegate'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { stripHtml } from '@hooks/useTracking/utils'
import useUser from '@hooks/useUser'
import useViewportTracking from '@hooks/useViewportTracking'
import CMPPlaceholder from '@components/CMPPlaceholder'

export interface EmbeddedContentType {
  url?: string
  ['max-width']?: number
  ['max-height']?: number
  ['max-height-mobile']?: number
  scrollingEnabled?: boolean
  htmlCode?: string
  withSSO?: boolean
  parentProps?: {
    inline?: boolean
  }
}

export interface EmbeddedContentWebProps extends EmbeddedContentType {
  heightFieldName?: string
  idFieldName?: string
  id?: string
  maxWidth?: number
  maxHeight?: number
  maxHeightMobile?: number
  trackImpression?: boolean
  title?: string
  contentId?: string
  className?: string
}

export interface CMPAwareEmbeddedContentProps extends EmbeddedContentWebProps {
  bypassCMP?: boolean
}

type StyledEmbeddedContentContainer = Pick<
  EmbeddedContentWebProps,
  'maxHeight' | 'maxHeightMobile'
>

const {
  iframe: { allowedOriginsToReceivePostMessage },
} = config

const constructIframeSrc = ({
  normalizedUrl,
  iframeId,
  queryClient,
}: {
  normalizedUrl?: string
  iframeId: string
  queryClient: QueryClient
}): string | undefined => {
  if (!normalizedUrl) {
    return
  }

  const parsedUrl = new URL(normalizedUrl)
  const iframeIdHashParam = `iframeId=${iframeId}`

  const currentTheme = getAppTheme(queryClient)

  // Check if the hash already has content (i.e., it's not empty or just '#')
  if (parsedUrl.hash && parsedUrl.hash.length > 1) {
    // If it does, append the new parameter with an ampersand
    parsedUrl.hash += `&${iframeIdHashParam}&theme=${currentTheme}`
  } else {
    // Otherwise, just set the hash to the new parameter.
    // The URL API will automatically add the '#' prefix.
    parsedUrl.hash = `${iframeIdHashParam}&theme=${currentTheme}`
  }

  return parsedUrl.toString()
}

const StyledEmbeddedContentContainer = styled.div<StyledEmbeddedContentContainer>`
  ${({ maxHeight, maxHeightMobile }) => css`
    width: 100%;
    position: relative;
    height: ${maxHeight ?? 200}px;

    ${mobileCSS(css`
      height: ${maxHeightMobile ?? 200}px;
    `)}
  `}
`

const StyledIFrame = styled.iframe`
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border: none;
  height: 100%;
  width: 1px;
  min-width: 100%;
  margin: 0;
`

const EmbeddedContent: FunctionComponent<EmbeddedContentWebProps> = ({
  url,
  scrollingEnabled,
  htmlCode,
  'max-height': maxHeight,
  'max-height-mobile': maxHeightMobile,
  withSSO,
  trackImpression,
  title,
  contentId,
  className,
}) => {
  const queryClient = useQueryClient()

  const [iframeType] = useState<'embed' | 'url' | 'sailthru-url'>(() => {
    if (htmlCode) {
      return 'embed'
    } else {
      try {
        const parsedUrl = new URL(url as string)
        if (
          ['link.blick.ch', 'link2.blick.ch'].includes(parsedUrl.hostname) &&
          parsedUrl.pathname.endsWith('signup-overview-with-redirect')
        ) {
          return 'sailthru-url'
        } else {
          return 'url'
        }
      } catch {
        return 'url'
      }
    }
  })

  const [normalizedUrl, setNormalizedUrl] = useState(() =>
    iframeType === 'url' ? url : undefined,
  )

  useEffect(() => {
    if (iframeType === 'url') {
      setNormalizedUrl(url)
    }
  }, [iframeType, url])

  const disableThirdPartyScripts = useDisableThirdPartyScripts()
  const user = useUser()
  const isAuthResolved = useIsAuthResolved()
  const iframeIdRef = useRef<string>('')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const { addIframe, removeIframe } = useIframeRefs()
  const [loadNow, setLoadNow] = useState(false)

  useEffect(() => {
    if (iframeType === 'sailthru-url' && !normalizedUrl && isAuthResolved) {
      setNormalizedUrl(
        user
          ? `https://link.blick.ch/manage/6ma/manage-all-subscriptions?email=${user.email}&hash=${user.s.blick}`
          : url,
      )
    }
  }, [iframeType, isAuthResolved, normalizedUrl, url, user])

  const sendUserData = useCallback(() => {
    if (withSSO && normalizedUrl) {
      // Only send the user data down to allowed origins
      // defined in the config by us
      const embeddedOrigin = new URL(normalizedUrl).origin
      if (
        (allowedOriginsToReceivePostMessage as unknown as string[]).includes(
          embeddedOrigin,
        )
      ) {
        iframeRef?.current?.contentWindow?.postMessage({ user }, embeddedOrigin)
      }
    }
  }, [normalizedUrl, user, withSSO])

  useEffect(() => {
    if ((iframeType === 'embed' || normalizedUrl) && loadNow) {
      const iframeRefObj = {
        id: iframeIdRef.current,
        ref: iframeRef,
        containerRef: embeddedContentContainerRef,
        url: constructIframeSrc({
          normalizedUrl,
          iframeId: iframeIdRef.current,
          queryClient,
        }),
        ...(withSSO && { callback: sendUserData }),
      }
      addIframe(iframeRefObj)
    }
    return () => {
      removeIframe(iframeIdRef.current)
    }
  }, [
    removeIframe,
    iframeRef,
    addIframe,
    iframeType,
    normalizedUrl,
    sendUserData,
    withSSO,
    loadNow,
    queryClient,
  ])

  const setIframeRef = useCallback<(node?: HTMLIFrameElement | null) => void>(
    (node) => {
      if (node) {
        iframeRef.current = node
      }
    },
    [],
  )

  const doLoadNow = useCallback<(isInView: boolean) => void>((isInView) => {
    if (isInView) {
      if (!iframeIdRef.current) {
        iframeIdRef.current = nanoid()
      }
      setLoadNow(true)
    }
  }, [])

  const { ref: setInViewEmbeddedContentContainerRef } = useInView({
    rootMargin: '100% 0px 100% 0px',
    onChange: doLoadNow,
    triggerOnce: true,
  })

  const embeddedContentContainerRef = useRef<HTMLDivElement>(undefined)
  const setEmbeddedContentContainerRef = useCallback<
    (node?: Element | null | undefined) => void
  >(
    (node) => {
      if (node) {
        embeddedContentContainerRef.current = node as HTMLDivElement
        setInViewEmbeddedContentContainerRef(node)
      }
    },
    [setInViewEmbeddedContentContainerRef],
  )

  const trackingOnImpression = useCallback<TrackingFnType>(
    () => ({
      event: 'iframe_banner',
      eventCategory: 'iframeBanner',
      eventLabel: `${contentId}:${stripHtml(title)}`,
      eventAction: 'impression',
      targetUrl: normalizedUrl,
    }),
    [contentId, title, normalizedUrl],
  )

  const handleImpressionTrack = useTracking(trackingOnImpression)

  const viewportTrackingRef = useViewportTracking({
    onImpression: handleImpressionTrack,
    track: trackImpression,
  })

  const ref = useRefDelegate(
    useMemo(
      () => [setIframeRef, viewportTrackingRef],
      [setIframeRef, viewportTrackingRef],
    ),
  )

  if (disableThirdPartyScripts) {
    return null
  }

  return (
    <StyledEmbeddedContentContainer
      {...(normalizedUrl?.endsWith('/quizwidget') ? { id: 'quizwidget' } : {})}
      {...(className ? { className } : {})}
      maxHeight={maxHeight}
      maxHeightMobile={maxHeightMobile}
      ref={setEmbeddedContentContainerRef}
    >
      {(iframeType === 'embed' || normalizedUrl) && loadNow && (
        <StyledIFrame
          id={iframeIdRef.current}
          ref={ref}
          {...(iframeType === 'embed'
            ? { srcDoc: htmlCode }
            : {
                src: constructIframeSrc({
                  normalizedUrl,
                  iframeId: iframeIdRef.current,
                  queryClient,
                }),
              })}
          title="Embedded Content"
          loading="eager"
          scrolling={scrollingEnabled ? 'yes' : 'no'}
          allowFullScreen
          data-chromatic="ignore"
        />
      )}
    </StyledEmbeddedContentContainer>
  )
}

const CMPAwareEmbeddedContent: FunctionComponent<
  CMPAwareEmbeddedContentProps
> = ({ bypassCMP, ...restOfProps }) => {
  let autoWhitelistUrl = false

  try {
    const { url } = restOfProps

    if (url) {
      const isBlickDomain = ['blick.ch'].some((domain) =>
        new URL(url as string).hostname.endsWith(domain),
      )

      const isStaticPage = ['/assets/dist/static'].some((pathSegment) =>
        new URL(url as string).pathname.includes(pathSegment),
      )

      autoWhitelistUrl = isBlickDomain && !isStaticPage
    }
  } catch {
    // Do nothing
  }

  const allCategoriesEnabled = useCMPCookieCategories(
    'allCategoriesEnabledChanged',
  )

  return autoWhitelistUrl || bypassCMP || allCategoriesEnabled ? (
    <EmbeddedContent {...restOfProps} />
  ) : (
    <CMPPlaceholder />
  )
}

const MemoizedCMPAwareEmbeddedContent = memo(CMPAwareEmbeddedContent)

export { StyledEmbeddedContentContainer }

export default MemoizedCMPAwareEmbeddedContent
